import { Ref, ref } from 'vue';
import { TasksKanbanColumnModel, DragModel, TaskMoveQueueRequestModel } from '../models';
import { TaskKanbanAdapterService } from '../adapter';

/**адаптер */
const adapter = new TaskKanbanAdapterService();

type TasksKanbanDraggbleCallbacks = {
  /**
   * * Перемещение задачи в другую колонку
   * @param taskId Id задачи
   * @param toColumn В какую колонку задача была перемещена
   * @param fromColumn Из какой колонки задача была перемещена
   * @returns
   */
  moveTask: (
    taskId: number,
    toColumn: TasksKanbanColumnModel,
    fromColumn: TasksKanbanColumnModel,
  ) => void;
  /**
   * * Сортировка задачи в колонке
   * @param taskId Id задачи
   * @param column Колонка
   * @param orderNumber Порядковый номер задачи в колонке
   * @returns
   */
  sortTasks: (taskId: number, column: TasksKanbanColumnModel, orderNumber: number) => void;
};

export const useTasksKanbanDraggble = (
  columns: Ref<Array<TasksKanbanColumnModel>>,
  callbacks: TasksKanbanDraggbleCallbacks,
) => {
  /**Перетаскивается ли элемент */
  const isDrag = ref<boolean>(false);
  /**Текущая перетаскиваемая задача */
  const currentDragTask = ref<DragModel<{ TaskId: number }>>();
  /**Индекс колонки из которой перетаскивается задача */
  const currentDragTaskColumnIndex = ref<number>();
  /**Флагг для очистки значенией (для сортировки) */
  const cleanFlag = ref(false);

  /**
   * * При любом перетаскивании
   * @param _isDrag Происходит ли перетаскивание
   * @param model Данные перетаскиваемого элемента
   * @param columnIndex Индекс перетаскиваемой колонки
   */
  const onDrag = async (
    _isDrag: boolean,
    model: DragModel<{ TaskId: number }>,
    columnIndex: number,
  ) => {
    if (!_isDrag) {
      setTimeout(() => {
        currentDragTask.value = undefined;
        currentDragTaskColumnIndex.value = undefined;
        isDrag.value = _isDrag;
        /**index задачи */
      });
    } else {
      isDrag.value = _isDrag;
      currentDragTask.value = model;
      currentDragTaskColumnIndex.value = columnIndex;
    }
  };

  /**
   * * При отмене перетаскивания
   * @param columnTaskIds Идентификаторы задач в колонке (для обновления)
   * @param columnIndex Индекс колонки
   */
  const onCancel = (columnTaskIds: Array<DragModel<{ TaskId: number }>>, columnIndex: number) => {
    columns.value[columnIndex].Data = columnTaskIds;
  };

  /**
   * * При сортировки в рамках одной колонки
   * @param columnIndex Индекс колонки
   * @param columnTaskIds Идентификаторы задач в колонке (для обновления)
   */

  const onChange = (
    columnIndex: number,
    columnTaskIds: Array<DragModel<{ TaskId: number }>>,
    columnData: DragModel<{ TaskId: number }>[],
  ) => {
    const column = columns.value[columnIndex];
    column.Data = columnTaskIds;
    // ID перемещаемой задачи
    const taskId = currentDragTask.value.Data.TaskId;
    // Порядковый номер задачи в колонке после перетаскивания
    const taskOrderNumber = column.Data.findIndex((dragData) => dragData.Data.TaskId === taskId);

    currentDragTask.value = undefined;
    currentDragTaskColumnIndex.value = undefined;
    isDrag.value = false;
    cleanFlag.value = !cleanFlag.value;

    if (taskOrderNumber !== -1) callbacks.sortTasks(taskId, column, taskOrderNumber);

    /**index задачи */
    const taskIndex = columnData.findIndex((item) => item.Data.TaskId === taskId);
    /**Задача выше */
    let prevId = columnData[taskIndex - 1]?.Data.TaskId;
    /**Задача ниже */
    let nextId = columnData[taskIndex + 1]?.Data.TaskId;

    if (prevId && nextId) nextId = null;

    adapter.taskMoveInQueueAsync(
      new TaskMoveQueueRequestModel({
        TaskMoveId: taskId,
        TaskUpId: prevId,
        TaskDownId: nextId,
      }),
    );
  };

  /**
   * * При перемещении из одной колонки в другую
   * @param columnIndex Индекс колонки
   * @param columnTaskIds Идентификаторы задач в колонке (для обновления)
   */
  const onAdded = async (
    columnIndex: number,
    columnTaskIds: Array<DragModel<{ TaskId: number }>>,
  ) => {
    // Если перемещение в ту же колонку
    if (columnIndex === currentDragTaskColumnIndex.value) return;

    const column = columns.value[columnIndex];
    column.Data = columnTaskIds;
    // ID перемещаемой задачи
    const taskId = currentDragTask.value.Data.TaskId;
    // Порядковый номер задачи в колонке после перетаскивания
    const taskOrderNumber = column.Data.findIndex((dragData) => dragData.Data.TaskId === taskId);

    const fromColumn = columns.value[currentDragTaskColumnIndex.value];
    if (fromColumn) {
      fromColumn.Data = fromColumn.Data.filter((x) => x.Data.TaskId != taskId);
    }

    currentDragTask.value = undefined;
    currentDragTaskColumnIndex.value = undefined;
    isDrag.value = false;
    cleanFlag.value = !cleanFlag.value;

    if (taskOrderNumber !== -1) callbacks.moveTask(taskId, column, fromColumn);
  };

  return {
    /**
     * * При любом перетаскивании
     * @param _isDrag Происходит ли перетаскивание
     * @param model Данные перетаскиваемого элемента
     * @param columnIndex Индекс перетаскиваемой колонки
     */
    onDrag,
    /**
     * * При отмене перетаскивания
     * @param columnTaskIds Идентификаторы задач в колонке (для обновления)
     * @param columnIndex Индекс колонки
     */
    onCancel,
    /**
     * * При сортировки в рамках одной колонки
     * @param columnIndex Индекс колонки
     * @param columnTaskIds Идентификаторы задач в колонке (для обновления)
     */
    onChange,
    /**
     * * При перемещении из одной колонки в другую
     * @param columnIndex Индекс колонки
     * @param columnTaskIds Идентификаторы задач в колонке (для обновления)
     */
    onAdded,
    /**Перетаскивается ли элемент */
    isDrag,
    /**Текущая перетаскиваемая задача */
    currentDragTask,
    /**Индекс колонки из которой перетаскивается задача */
    currentDragTaskColumnIndex,
    /**Флагг для очистки значенией (для сортировки) */
    cleanFlag,
  };
};
